import { z } from 'zod'

/** Gives a course credit (POST); with `?learnerId=`, lists a person's (GET). */
export const courseCreditsApi = '/learner-history/v1/course-credits'

/** Giving and removing course credits: the `course-credits` aspect, held at global reach only. */
export const writeCreditsPermission = 'learner-history.learner-records.course-credits.write'
export const deleteCreditsPermission = 'learner-history.learner-records.course-credits.delete'

/** A person's course credits. */
export function learnerCreditsPath(userId: string): string {
  return `${courseCreditsApi}?learnerId=${encodeURIComponent(userId)}`
}

/** One course credit, which DELETE removes. */
export function courseCreditPath(creditId: string): string {
  return `${courseCreditsApi}/${encodeURIComponent(creditId)}`
}

/** A person's credit for a course. */
export interface CourseCredit {
  /** Such as `ccr_2y1VdX9p7k3FZ8mH6nQaRs`. */
  readonly id: string
  readonly courseId: string
  /** `null` when Catalog no longer knows the course. */
  readonly courseName: string | null
}

const creditSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  courseName: z.string().nullish(),
})

/** Narrows one course credit. */
export function parseCourseCredit(body: unknown): CourseCredit {
  const credit = creditSchema.parse(body)
  return { id: credit.id, courseId: credit.courseId, courseName: credit.courseName ?? null }
}

/** Narrows a person's course credits, which come as one page. */
export function parseCourseCredits(body: unknown): readonly CourseCredit[] {
  return z.object({ data: z.array(z.unknown()) }).parse(body).data.map(parseCourseCredit)
}

/** A person's credit for a course, with when it was given, as User Details lists it. */
export interface GivenCourseCredit extends CourseCredit {
  /** ISO 8601. */
  readonly givenOn: string
}

const givenOnSchema = z.object({ givenOn: z.string() })

/** Narrows a person's course credits with when each was given, which come as one page. */
export function parseGivenCourseCredits(body: unknown): readonly GivenCourseCredit[] {
  return z
    .object({ data: z.array(z.unknown()) })
    .parse(body)
    .data.map((item) => {
      const { id, courseId, courseName } = parseCourseCredit(item)
      return { id, courseId, courseName, givenOn: givenOnSchema.parse(item).givenOn }
    })
}
